/**
 * Workspace context reset contract.
 *
 * When the active workspace context changes (workspace switch) or the acting
 * identity changes (persona switch), every workspace-scoped cache and open
 * surface must be invalidated so no data leaks across investor-group contexts.
 *
 * The runtime demo has a single workspace, so this contract is exercised by
 * synthetic fixtures/tests today; the interface is production-ready and each
 * consumer registers a reset handler.
 */

/** The distinct caches/surfaces that must be reset on a workspace change. */
export type WorkspaceScopedCache =
  | 'dashboard'
  | 'companies'
  | 'projects'
  | 'tasks'
  | 'taskInspector'
  | 'documents'
  | 'documentInspector'
  | 'dataRooms'
  | 'investments'
  | 'reports'
  | 'navigation'
  | 'pendingAsyncResponses';

export const WORKSPACE_SCOPED_CACHES: readonly WorkspaceScopedCache[] = [
  'dashboard',
  'companies',
  'projects',
  'tasks',
  'taskInspector',
  'documents',
  'documentInspector',
  'dataRooms',
  'investments',
  'reports',
  'navigation',
  'pendingAsyncResponses',
] as const;

export interface WorkspaceContextChange {
  readonly previousWorkspaceId: string | null;
  readonly nextWorkspaceId: string | null;
  readonly previousUserId: string | null;
  readonly nextUserId: string | null;
}

export type WorkspaceResetHandler = (change: WorkspaceContextChange) => void;

/**
 * Registry of reset handlers keyed by the cache they own. Consumers register
 * once; `runWorkspaceContextReset` invokes every registered handler when the
 * workspace or identity changes.
 */
class WorkspaceContextResetRegistry {
  private readonly handlers = new Map<WorkspaceScopedCache, Set<WorkspaceResetHandler>>();

  register(cache: WorkspaceScopedCache, handler: WorkspaceResetHandler): () => void {
    const set = this.handlers.get(cache) ?? new Set<WorkspaceResetHandler>();
    set.add(handler);
    this.handlers.set(cache, set);
    return () => {
      set.delete(handler);
    };
  }

  /** Which caches currently have at least one registered handler. */
  registeredCaches(): WorkspaceScopedCache[] {
    return [...this.handlers.entries()]
      .filter(([, set]) => set.size > 0)
      .map(([cache]) => cache);
  }

  clear(): void {
    this.handlers.clear();
  }

  /**
   * Determine whether a change requires a reset. A reset is required when the
   * workspace id OR the acting user id changes.
   */
  requiresReset(change: WorkspaceContextChange): boolean {
    return (
      change.previousWorkspaceId !== change.nextWorkspaceId ||
      change.previousUserId !== change.nextUserId
    );
  }

  /**
   * Invoke every registered handler for a qualifying change. Returns the list
   * of caches that were reset (empty when no reset was required).
   */
  run(change: WorkspaceContextChange): WorkspaceScopedCache[] {
    if (!this.requiresReset(change)) return [];
    const reset: WorkspaceScopedCache[] = [];
    for (const [cache, set] of this.handlers.entries()) {
      for (const handler of set) handler(change);
      if (set.size > 0) reset.push(cache);
    }
    return reset;
  }
}

export const workspaceContextResetRegistry = new WorkspaceContextResetRegistry();

/** Convenience wrapper mirroring the registry `run` method. */
export function runWorkspaceContextReset(
  change: WorkspaceContextChange,
): WorkspaceScopedCache[] {
  return workspaceContextResetRegistry.run(change);
}
